import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../config/axios";
import { ReservaEstacionamiento } from "../../../types/ReservaEstacionamiento";
import { logger } from "../../../utils/logger";
import websocketService from "../../../services/websocket";
import { invalidateParkingReservationQueries } from "../../../utils/queryInvalidation";

const fetchReservasEstacionamiento = async (
  year: number,
  month: number
): Promise<ReservaEstacionamiento[]> => {
  try {
    const { data } = await api.get(
      `/reservas-estacionamiento/${year}/${month + 1}`
    );
    return data;
  } catch (error) {
    logger.error("Error fetching reservas de estacionamiento:", error);
    throw new Error("No se pudieron cargar las reservas de estacionamiento.");
  }
};

export const useMonthEstacionamientoReservations = (
  year: number,
  month: number
) => {
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isInvalidatingRef = useRef(false);

  const query = useQuery({
    queryKey: ["reservasEstacionamiento", year, month],
    queryFn: () => fetchReservasEstacionamiento(year, month),
  });

  // 🚀 Configurar WebSocket para actualizaciones en tiempo real
  useEffect(() => {
    unsubscribeRef.current = websocketService.onReservaEstacionamientoChange((type, data) => {
      logger.debug(`🚗 Estacionamiento WebSocket Event: ${type}`, data);

      if (isInvalidatingRef.current) {
        logger.debug(
          "🚗 Estacionamiento WebSocket: Skipping invalidation (already invalidating)"
        );
        return;
      }

      isInvalidatingRef.current = true;
      invalidateParkingReservationQueries(queryClient);

      setTimeout(() => {
        isInvalidatingRef.current = false;
      }, 1000);
    });

    // Cleanup al desmontar
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [queryClient]);

  return query;
};
